/**
 * AURELINE CUSTOM FEATURES
 * 
 * - Viewing Counter: Display "X People are viewing this product"
 * - Delivery Date: Show delivery date (5 business days forward)
 * - Care Instructions: Display care blocks with icons and text
 */

// =====================================================
// 1. VIEWING COUNTER - Display people viewing product
// =====================================================

class ViewingCounter {
  constructor() {
    this.numbers = [];
    this.counterElement = null;
    this.init();
  }

  init() {
    // Get the viewing numbers from theme settings
    const viewingNumbersString = document.documentElement.getAttribute('data-viewing-numbers');
    
    if (!viewingNumbersString) return;

    try {
      // Parse comma-separated numbers: "7,9,10,11,13,1,4,5,8.15"
      this.numbers = viewingNumbersString
        .split(',')
        .map(num => parseFloat(num.trim()))
        .filter(num => !isNaN(num));

      if (this.numbers.length === 0) return;

      // Display initial counter
      const randomViewers = this.getRandomNumber();
      this.displayViewingCounter(randomViewers);
      
      // Update counter every 3-5 seconds
      this.startAutoUpdate();
    } catch (error) {
      console.warn('Error parsing viewing numbers:', error);
    }
  }

  getRandomNumber() {
    return this.numbers[Math.floor(Math.random() * this.numbers.length)];
  }

  startAutoUpdate() {
    const updateViewCount = () => {
      if (this.counterElement) {
        const newCount = this.getRandomNumber();
        const textElement = this.counterElement.querySelector('.viewing-counter__text');
        if (textElement) {
          textElement.textContent = `${newCount} people are viewing this product right now`;
          // Add animation effect
          this.counterElement.style.opacity = '0.5';
          setTimeout(() => {
            this.counterElement.style.opacity = '1';
          }, 150);
        }
      }
      // Schedule next update with random interval 3-5 seconds
      setTimeout(updateViewCount, Math.random() * 2000 + 5000);
    };
    // Start the update cycle
    setTimeout(updateViewCount, Math.random() * 2000 + 5000);
  }

  displayViewingCounter(count) {
    // Look for the product title or insert near it
    const productTitle = document.querySelector('[data-product-title]') || 
                         document.querySelector('h1') ||
                         document.querySelector('[class*="product-title"]');
    
    if (!productTitle) return;

    const counterHTML = `
      <div class="viewing-counter" data-viewing-counter style="transition: opacity 0.2s ease-in-out;">
        <div class="viewing-counter__content">
          <svg class="viewing-counter__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
          <span class="viewing-counter__text">${count} people are viewing this product right now</span>
        </div>
      </div>
    `;

    // Insert before or after product title
    if (productTitle.parentNode) {
      this.counterElement = this.createElementFromHTML(counterHTML);
      productTitle.parentNode.insertBefore(
        this.counterElement,
        productTitle
      );
    }
  }

  createElementFromHTML(htmlString) {
    const div = document.createElement('div');
    div.innerHTML = htmlString.trim();
    return div.firstChild;
  }
}

// =====================================================
// 2. DELIVERY DATE - Calculate & display delivery date
// =====================================================

class DeliveryDateCalculator {
  constructor() {
    this.init();
  }

  init() {
    // Check if delivery date is enabled (from section setting)
    const deliveryEnabled = document.documentElement.getAttribute('data-show-delivery-date');
    if (deliveryEnabled !== 'true') return;

    const deliveryElement = this.findOrCreateDeliveryDateContainer();
    if (!deliveryElement) return;

    const deliveryDate = this.calculateDeliveryDate();
    this.displayDeliveryDate(deliveryDate, deliveryElement);
  }

  findOrCreateDeliveryDateContainer() {
    // Check if container already exists
    let container = document.querySelector('[data-delivery-date-container]');
    if (container) return container;

    // Find the buy-buttons block (Add to Cart button location)
    const buyButtonsBlock = document.querySelector('[data-block-type="buy-buttons"]') ||
                           document.querySelector('.buy-buttons-block') ||
                           document.querySelector('[class*="buy-buttons"]');
    
    if (buyButtonsBlock && buyButtonsBlock.parentNode) {
      // Create the container
      container = document.createElement('div');
      container.setAttribute('data-delivery-date-container', '');
      container.className = 'delivery-date-container-wrapper';
      
      // Insert after buy-buttons block
      if (buyButtonsBlock.nextSibling) {
        buyButtonsBlock.parentNode.insertBefore(container, buyButtonsBlock.nextSibling);
      } else {
        buyButtonsBlock.parentNode.appendChild(container);
      }
      
      return container;
    }

    // Fallback: look for product-details container
    const productDetails = document.querySelector('.product-details') ||
                          document.querySelector('[data-testid="product-information-details"]');
    if (productDetails) {
      container = document.createElement('div');
      container.setAttribute('data-delivery-date-container', '');
      container.className = 'delivery-date-container-wrapper';
      productDetails.appendChild(container);
      return container;
    }

    return null;
  }

  calculateDeliveryDate() {
    // Calculate 5 business days from today
    let daysToAdd = 5;
    let currentDate = new Date();

    while (daysToAdd > 0) {
      currentDate.setDate(currentDate.getDate() + 1);
      
      // Check if it's a weekday (Monday = 1, Friday = 5)
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) { // exclude Sunday (0) and Saturday (6)
        daysToAdd--;
      }
    }

    return currentDate;
  }

  formatDate(date) {
    // Format as: "April 10, 2026"
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  }

  displayDeliveryDate(date, container) {
    const formattedDate = this.formatDate(date);
    
    container.innerHTML = `
      <div class="delivery-date-info">
        <svg class="delivery-date-info__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="6" width="18" height="14" rx="2"></rect>
          <path d="M7 6V4a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2"></path>
          <path d="M9 12h6"></path>
        </svg>
        <span class="delivery-date-info__text">Order now to get delivered by <strong>${formattedDate}</strong></span>
      </div>
    `;
  }
}

// =====================================================
// 3. CARE INSTRUCTIONS - Display care blocks
// =====================================================

class CareInstructionsBlock {
  constructor() {
    this.init();
  }

  init() {
    // Care blocks are handled via Liquid, but this can be used
    // for any dynamic interactions if needed
    this.setupCareAnimations();
  }

  setupCareAnimations() {
    const careBlocks = document.querySelectorAll('[data-care-instruction-block]');
    
    careBlocks.forEach((block, index) => {
      // Add staggered animation delay
      block.style.animationDelay = `${index * 0.1}s`;
      
      // Add intersection observer for lazy animation
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        }, { threshold: 0.1 });
        
        observer.observe(block);
      }
    });
  }
}

// =====================================================================
// INITIALIZATION - Run all custom features when DOM is ready
// =====================================================================

function injectCustomStyles() {
  // Inject CSS styles for delivery date and other custom elements
  const styleId = 'aureline-custom-styles';
  if (document.getElementById(styleId)) return; // Avoid duplicate styles

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    /* Delivery Date Styles */
    .delivery-date-container-wrapper {
      margin-top: 0;
      margin-bottom: 24px;
    }

    .delivery-date-info {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 16px;
      background-color: rgba(var(--color-primary-rgb), 0.08);
      border-radius: 4px;
      animation: slideInDown 0.4s ease-out;
    }

    .delivery-date-info__icon {
      flex-shrink: 0;
      color: var(--color-primary);
    }

    .delivery-date-info__text {
      font-size: 0.95rem;
      color: var(--color-foreground);
      line-height: 1.4;
    }

    .delivery-date-info__text strong {
      color: var(--color-primary);
      font-weight: 600;
    }

    @keyframes slideInDown {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @media (max-width: 640px) {
      .delivery-date-info {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
      }

      .delivery-date-info__icon {
        order: 2;
        margin-left: auto;
      }
    }

    /* Viewing Counter Styles */
    .viewing-counter {
      margin-bottom: 16px;
      animation: fadeInDown 0.4s ease-out;
    }

    .viewing-counter__content {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 0.85rem;
      color: var(--color-primary);
      font-weight: 500;
    }

    .viewing-counter__icon {
      color: var(--color-primary);
    }

    @keyframes fadeInDown {
      from {
        opacity: 0;
        transform: translateY(-8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;
  document.head.appendChild(style);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    injectCustomStyles();
    new ViewingCounter();
    new DeliveryDateCalculator();
    new CareInstructionsBlock();
  });
} else {
  // DOM is already loaded
  injectCustomStyles();
  new ViewingCounter();
  new DeliveryDateCalculator();
  new CareInstructionsBlock();
}

// Support for Shopify's dynamic sections (AJAX loads)
document.addEventListener('shopify:section:load', () => {
  injectCustomStyles();
  new ViewingCounter();
  new DeliveryDateCalculator();
  new CareInstructionsBlock();
});
